import { useState, useEffect } from 'react';
import { doc, getDoc, collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { Bell, X, Clock, User, Calendar } from 'lucide-react';
import { useUserSession } from '../../context/UserSessionContext';

const CustomerActionsNotifier = () => {
  const { effectiveUserId } = useUserSession();

  const [userId, setUserId] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [actions, setActions] = useState([]);
  const [hasNewActions, setHasNewActions] = useState(false);
  const [newActionIds, setNewActionIds] = useState(new Set());
  const [lastLocalTimestamp, setLastLocalTimestamp] = useState(null);
  const [loading, setLoading] = useState(false);

  // Cache key for local storage
  const cacheKey = userId ? `customer-actions-timestamp-${userId}` : null;

  // Fetch userId from AuthContext
  useEffect(() => {
    const fetchUserId = async () => {
      try {
        const id = effectiveUserId;
        setUserId(id);
      } catch (error) {
        console.error('Error fetching user ID:', error);
      }
    };

    fetchUserId();
  }, [effectiveUserId]);

  // Load cached timestamp on mount
  useEffect(() => {
    if (!cacheKey) return;

    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      setLastLocalTimestamp(new Date(cached));
    }
  }, [cacheKey]);

  // Poll for timestamp updates
  useEffect(() => {
    if (!userId) return;

    const checkForUpdates = async () => {
      try {
        const timestampDoc = await getDoc(doc(db, 'customerActions', userId));

        if (timestampDoc.exists()) {
          const serverTimestamp = timestampDoc.data().lastUpdate?.toDate();

          if (serverTimestamp && (!lastLocalTimestamp || serverTimestamp > lastLocalTimestamp)) {
            await fetchActions(serverTimestamp);
          }
        }
      } catch (error) {
        console.error('Error checking for updates:', error);
      }
    };

    // Initial check
    checkForUpdates();

    // Poll every 30 seconds
    const interval = setInterval(checkForUpdates, 30000);

    return () => clearInterval(interval);
  }, [userId, lastLocalTimestamp]);

  const fetchActions = async (serverTimestamp) => {
    try {
      setLoading(true);

      const actionsRef = collection(db, 'customerActions', userId, 'actions');
      const actionsQuery = query(actionsRef, orderBy('timestamp', 'desc'), limit(50));

      // Use onSnapshot for real-time updates
      const unsubscribe = onSnapshot(actionsQuery, (snapshot) => {
        const fetchedActions = [];
        const newIds = new Set();

        snapshot.forEach((doc) => {
          const actionData = doc.data();

          // Debug log to see what's causing the object error
          console.log('Action data:', {
            id: doc.id,
            actionType: actionData.actionType,
            customerName: actionData.customerName,
            customerPhone: actionData.customerPhone,
            serviceName: actionData.serviceName,
            timestamp: actionData.timestamp,
            metadata: actionData.metadata
          });

          const action = { id: doc.id, ...actionData };
          fetchedActions.push(action);

          // Mark as new if timestamp is newer than cached timestamp
          if (lastLocalTimestamp && action.timestamp?.toDate() > lastLocalTimestamp) {
            newIds.add(doc.id);
          }
        });

        setActions(fetchedActions);

        if (newIds.size > 0) {
          setNewActionIds(newIds);
          setHasNewActions(true);
          showNotification(`${newIds.size} new customer action${newIds.size > 1 ? 's' : ''}`);
        }

        // Update local cache timestamp
        if (serverTimestamp && cacheKey) {
          localStorage.setItem(cacheKey, serverTimestamp.toISOString());
          setLastLocalTimestamp(serverTimestamp);
        }

        setLoading(false);
      });

      return unsubscribe;
    } catch (error) {
      console.error('Error fetching actions:', error);
      setLoading(false);
    }
  };

  const showNotification = (message) => {
    // Simple notification - you might want to use a toast library
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('Customer Actions', { body: message });
    }
  };

  const handleBellClick = () => {
    setIsExpanded(!isExpanded);

    if (!isExpanded) {
      // Mark new actions as seen when expanding
      setHasNewActions(false);
      setNewActionIds(new Set());
    }
  };

  const formatActionType = (actionType) => {
    const hebrewTranslations = {
      'appointment_created': 'תור נקבע',
      'appointment_cancelled': 'תור בוטל',
      'appointment_updated': 'תור עודכן',
      'appointment_confirmed': 'תור אושר',
      'appointment_completed': 'תור הושלם',
      'appointment_rescheduled': 'תור נדחה',
      'customer_registered': 'לקוח נרשם',
      'payment_received': 'תשלום התקבל',
      'service_booked': 'שירות הוזמן'
    };

    return hebrewTranslations[actionType] || actionType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'זמן לא ידוע';

    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'עכשיו';
      if (diffMins < 60) return `לפני ${diffMins} דקות`;
      if (diffHours < 24) return `לפני ${diffHours} שעות`;
      if (diffDays < 7) return `לפני ${diffDays} ימים`;

      // Format as Hebrew date
      return date.toLocaleDateString('he-IL', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting timestamp:', error);
      return 'זמן לא ידוע';
    }
  };

  const formatDateTime = (date, time) => {
    if (!date) return null;

    try {
      let dateObj;

      // Handle various date formats using built-in Date constructor
      if (time) {
        // Combine date and time if both provided
        dateObj = new Date(`${date}T${time}`);
      } else {
        // Just date
        dateObj = new Date(date);
      }

      // Check if date is valid
      if (isNaN(dateObj.getTime())) {
        return `${date}${time ? ` בשעה ${time}` : ''}`;
      }

      const dateStr = dateObj.toLocaleDateString('he-IL', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });

      const timeStr = time ? ` בשעה ${time}` : '';
      return dateStr + timeStr;

    } catch (error) {
      return `${date}${time ? ` בשעה ${time}` : ''}`;
    }
  };

  const getActionIcon = (actionType) => {
    switch (actionType) {
      case 'appointment_created':
        return <Calendar className="w-4 h-4 text-green-500" />;
      case 'appointment_cancelled':
        return <X className="w-4 h-4 text-red-500" />;
      case 'appointment_updated':
        return <Clock className="w-4 h-4 text-blue-500" />;
      default:
        return <User className="w-4 h-4 text-gray-500" />;
    }
  };

  if (!userId) return null;

  return (
    <div className="relative">
      {/* Bell Button */}
      <button
        onClick={handleBellClick}
        className="relative p-2 rounded-full bg-white shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-200 group"
      >
        <Bell className={`w-5 h-5 transition-colors ${hasNewActions ? 'text-purple-600' : 'text-gray-600 group-hover:text-purple-600'}`} />

        {hasNewActions && (
          <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-pulse" />
        )}
      </button>

      {/* Expanded Actions List */}
      {isExpanded && (
        <div className="absolute top-12 left-0 w-72 sm:w-80 lg:w-96 max-w-[calc(100vw-2rem)] max-h-96 bg-white/95 backdrop-blur-sm border border-gray-200 rounded-2xl shadow-2xl overflow-hidden z-50">
          <div className="p-4 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Customer Actions</h3>
              <button
                onClick={() => setIsExpanded(false)}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-gray-500">
                <div className="animate-spin w-6 h-6 border-2 border-purple-600 border-t-transparent rounded-full mx-auto mb-2" />
                טוען פעולות...
              </div>
            ) : actions.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                <Bell className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                לא נמצאו פעולות לקוח
              </div>
            ) : (
              actions.map((action) => (
                <div
                  key={action.id}
                  className={`p-4 border-b border-gray-50 last:border-b-0 transition-colors ${newActionIds.has(action.id)
                    ? 'bg-purple-50/50 border-l-4 border-l-purple-500'
                    : 'hover:bg-gray-50/50'
                    }`}
                >
                  <div className="flex items-start gap-3">
                    {getActionIcon(action.actionType)}

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {formatActionType(String(action.actionType || ''))}
                        </p>
                        <span className="text-xs text-gray-500 flex-shrink-0">
                          {formatTimestamp(action.timestamp)}
                        </span>
                      </div>

                      <p className="text-xs text-gray-600 mb-1">
                        <span className="font-medium">{String(action.customerName || 'Unknown Customer')}</span>
                        <span className="text-gray-400 mx-1">•</span>
                        <span>{String(action.customerPhone || '')}</span>
                      </p>

                      {action.serviceName && (
                        <p className="text-xs text-purple-600 mb-1">
                          <span className="font-medium">{String(action.serviceName)}</span>
                        </p>
                      )}

                      {action.metadata && (
                        <div className="mt-2 text-xs text-gray-500">
                          {action.metadata.actionDate && action.metadata.actionTime && (
                            <span className="inline-block bg-blue-100 px-2 py-1 rounded mr-1">
                              פעולה: {formatDateTime(action.metadata.actionDate, action.metadata.actionTime)}
                            </span>
                          )}
                          {action.metadata.appointmentDate && action.metadata.appointmentTime && (
                            <span className="inline-block bg-gray-100 px-2 py-1 rounded mr-1">
                              תור: {formatDateTime(action.metadata.appointmentDate, action.metadata.appointmentTime)}
                            </span>
                          )}
                          {action.metadata.cancelledBy && (
                            <span className="inline-block bg-red-100 text-red-700 px-2 py-1 rounded mr-1">
                              בוטל על ידי {action.metadata.cancelledBy === 'customer' ? 'הלקוח' : action.metadata.cancelledBy}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerActionsNotifier;
